//Formik
import { useField } from "formik"
import QuestionOptionEntry from "./QuestionOptionEntry"
import { Button } from "primereact/button"

//Styles
import assessStyles from "../../../../../styles/assessments/Assessment.module.css"
import { getNewQuestionOption } from "../helpers/elementHelpers"
import elementTypes from "../Elements/elementTypes"
import QuestionLabelEntry from "./QuestionLabelEntry"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { useState } from "react"
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  rectIntersection,
  DragOverlay,
} from "@dnd-kit/core"
import { MoveOrderEntryToNewIndex } from "../helpers/dndElementHelper"
import QuestionOptionEntrySmall from "./QuestionOptionEntrySmall"

export default function QuestionOptions({
  formikQuestionName,
  onDelete,
  questionType,
  isLabel,
  disabled,
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  )

  const optionsFieldName = `${formikQuestionName}.${
    isLabel ? "questionLabels" : "options"
  }`
  const [field, , helpers] = useField(optionsFieldName)

  const [dndOption, setDndOption] = useState(undefined)
  const [tempOptions, setTempOptions] = useState(undefined)

  const useOptionsCopy = JSON.parse(JSON.stringify(field.value))

  const activeOptions = (tempOptions || useOptionsCopy || []).filter(
    (option) => !option.isDeleted
  )

  const isMatrix =
    questionType === elementTypes.matrixCheckboxElementId ||
    questionType === elementTypes.matrixElementId

  let label = "Options"
  let buttonTooltip = "Add a new option to this question"
  let buttonText = "+ Add option"
  let newOptionName = "new option"

  if (isMatrix) {
    label = isLabel ? "Rows" : "Columns"
    buttonTooltip = isLabel
      ? "Add a new row to this matrix"
      : "Add a new column to this matrix"
    buttonText = isLabel ? "+ Add Row" : "+ Add Column"
    newOptionName = isLabel ? "new row" : "new column"
  }

  return (
    <div>
      <label>{label}</label>
      <DndContext
        sensors={disabled ? undefined : sensors}
        collisionDetection={rectIntersection}
        onDragStart={(event) => {
          if (disabled) {
            return null
          }

          if (event.active.data.current.option) {
            setDndOption(event.active.data.current.option)
          } else {
            setDndOption(undefined)
          }
        }}
        onDragMove={(event) => {
          if (disabled) {
            return null
          }

          if (event.over && event.active) {
            if (event.over.id !== event.active.id) {
              let newOptions = tempOptions || activeOptions

              newOptions = MoveOrderEntryToNewIndex(
                newOptions,
                event.active.id,
                event.over.id
              )

              setTempOptions(newOptions)
            }
          } else {
            setTempOptions(undefined)
          }
        }}
        onDragEnd={(event) => {
          if (disabled) {
            return null
          }

          if (event.over && event.active && tempOptions) {
            helpers.setValue(tempOptions)
            setTempOptions(undefined)
          }
        }}
      >
        <DragOverlay>
          <QuestionOptionEntrySmall option={dndOption} />
        </DragOverlay>
        <SortableContext
          items={activeOptions.map((option) => option.id)}
          strategy={verticalListSortingStrategy}
          disabled={disabled}
        >
          {activeOptions.map((option, index) => {
            if (!option.isDeleted) {
              return isLabel ? (
                <QuestionLabelEntry
                  option={option}
                  options={field.value}
                  formikParentName={optionsFieldName}
                  onOptionDelete={() => {
                    onDelete(option.id)
                  }}
                  disabled={disabled}
                  key={option.id}
                />
              ) : (
                <QuestionOptionEntry
                  option={option}
                  options={field.value}
                  formikParentName={optionsFieldName}
                  onOptionDelete={() => {
                    onDelete(option.id)
                  }}
                  disabled={disabled}
                  key={option.id}
                />
              )
            } else {
              return null
            }
          })}
        </SortableContext>
      </DndContext>
      <div>
        <Button
          className={assessStyles.actionButton}
          type="button"
          tooltip={buttonTooltip}
          tooltipOptions={{ position: "bottom" }}
          onClick={() => {
            let newOption = getNewQuestionOption(newOptionName)
            newOption.orderNumber = Math.max(
              ...field.value
                .filter((option) => !option.isDeleted)
                .map((o) => o.orderNumber)
            )

            let options = [...field.value, newOption]
            helpers.setValue(options)
          }}
          disabled={disabled}
        >
          {buttonText}
        </Button>
      </div>
    </div>
  )
}
